// `redirect.page.tpl.html` and `host-redirect.page.tpl.html` — the two "Redirecting…" screens
// the extension shows while it hands the user off somewhere else.
//
// ⚠️ TWO SCREENS, ONE FILE, BECAUSE THEY ARE THE SAME MARKUP WITH ONE BRANCH ADDED. A
// whitespace-insensitive diff of the two templates is the host one's success state: a title
// that reads "Redirected" instead of "Redirecting...", and a message naming the ATS. Everything
// else — the logo, the spinner, the "click here" line, every class — is identical. Two files
// would be one copy of the other. The ELEMENT names still differ, and that part matters:
// `_redirect.page.scss` and `_host-redirect.page.scss` are separate files written against
// separate class names.
//
// ⚠️ THE TWO SCREENS RENDER DIFFERENT STATES FOR THE SAME "NOTHING HAPPENED", and their
// reference captures prove it. Both were captured with no parameters at all:
//
//   - `/redirect` shows the SPINNER. Its controller awaits `extensionExternalMessageService`
//     for an answer from the extension, and with no extension installed the answer never comes
//     — so `loading` stays true and the `finally` that would clear it never runs.
//   - `/host-redirect` shows "Redirected". Its controller switches on the redirect TYPE and its
//     default branch is `$q.resolve()` — an immediate success — so with no `type` in the URL it
//     reports a handoff that had nothing to hand off.
//
// Neither is a bug to fix here, and reproducing them is the point: a port that showed a spinner
// on both would be the same screen twice and a 5.25% diff on the second.
//
// ⚠️ AND NEITHER ACTUALLY REDIRECTS YET. The destinations are the shell's (Phase 6.3) — an
// external message to open the popup, or `ShortlistJobOpenInAtsService` to reach the ATS tab.
// `onRedirect` is where that lands; without it the screens behave exactly as above, which is
// what a browser without the extension does upstream too.
import { useEffect, useState, type ReactElement } from 'react'
import { PageHost } from '../auth/PageHost.tsx'
import { asset } from '../assetBase.ts'

/** `HostRedirectType` — the three the host screen knows how to perform. */
export const HOST_REDIRECT_TYPES = ['EXTERNAL_JOB', 'OPEN_EXTENSION_JOB', 'OPEN_EXTENSION_JOB_EDIT'] as const

export interface RedirectPageProps {
  /** The host variant — `chrome-extension-host-redirect-page`, with its success state. */
  host?: boolean
  /**
   * `$stateParams.type` on the host screen. An UNKNOWN or absent one is upstream's `default`
   * branch, which resolves immediately and reports success; a known one needs the handler.
   */
  redirectType?: string
  /**
   * `ctrl.atsLabel` — named in the success message ("Go to your Bullhorn browser tab"). The ATS
   * LABEL, not `atsName` (5cfd3ef899): see `getAtsLabel`.
   */
  atsLabel?: string
  /** Performs the handoff. Absent, a known type stays loading rather than claiming success. */
  onRedirect?: () => Promise<void>
}

export function RedirectPage({ host = false, redirectType, atsLabel, onRedirect }: RedirectPageProps): ReactElement {
  const known = redirectType !== undefined && (HOST_REDIRECT_TYPES as readonly string[]).includes(redirectType)
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading')

  useEffect(() => {
    if (!host) return undefined
    let live = true
    if (onRedirect === undefined) {
      // The default branch, and only the default branch. A known type with no handler has not
      // been performed, so it stays on the spinner rather than reporting a handoff that did
      // not happen — the one state this screen must never show falsely.
      if (!known) setState('success')
      return undefined
    }
    setState('loading')
    onRedirect().then(
      () => {
        if (live) setState('success')
      },
      () => {
        if (live) setState('error')
      },
    )
    return () => {
      live = false
    }
  }, [host, known, onRedirect])

  // `ng-if="ctrl.loading || ctrl.error"` on three of the four blocks: the error state shows the
  // SAME copy as loading, with the TODO upstream leaves on it about whether that is right.
  const redirecting = !host || state !== 'success'
  const redirected = host && state === 'success'

  const clickHere = (
    <>
      {/* ⚠️ ONE JSX LINE FOR THE SPACE BEFORE THE LINK. Split across lines, JSX drops the
          whitespace between the text and the anchor and it reads "clickhere". */}
      If the page does not redirect automatically, click{' '}
      <a
        href=""
        className="open-now"
        onClick={(event) => {
          event.preventDefault()
          void onRedirect?.()
        }}
      >
        here
      </a>
    </>
  )

  return (
    <PageHost name={host ? 'chrome-extension-host-redirect-page' : 'chrome-extension-redirect-page'}>
      <img className="logo" src={asset('/static/assets/img/woo/logo.svg')} alt="" />
      <div className="title">
        {host ? (
          <>
            {redirecting ? <div>Redirecting...</div> : null}
            {redirected ? <div>Redirected</div> : null}
          </>
        ) : (
          'Redirecting...'
        )}
      </div>
      {redirecting ? <i className="far fa-spinner fa-spin woo-loader" /> : null}
      <div className="message">
        {/* ⚠️ ONE EXTRA DIV ON THE HOST SCREEN, in the markup rather than in a stylesheet:
            `host-redirect` puts each half of its message inside its own `ng-if` div, and
            `redirect` has the text directly in `.message`. */}
        {redirecting ? host ? <div>{clickHere}</div> : clickHere : null}
        {redirected ? (
          <div>
            Matching candidates are ready for your review.
            <br />
            {/* ⚠️ ONE TEMPLATE LITERAL, NOT THREE CHILDREN. `Go to your {atsLabel} browser tab`
                renders as three text nodes and their advance widths accumulate differently from
                upstream's single interpolated text node — 37 pixels on this line, the whole
                difference between 0.01% and 0.00%. Fifth time in this port. */}
            {`Go to your ${atsLabel ?? ''} browser tab to review them now.`}
            <br />
            You may close this page.
          </div>
        ) : null}
      </div>
    </PageHost>
  )
}
